import {
  type IPaginationMetaProps,
  type IPaginationResponseData,
  type IResponseData,
  api,
} from './api';
import { globalDocumentPath } from './courses';

export const SVG_CATEGORIES = {
  ICONS: 'icons',
  PEOPLE: 'people',
  SHAPES: 'shapes',
} as const;

export type SvgCategory = (typeof SVG_CATEGORIES)[keyof typeof SVG_CATEGORIES];

export const SVG_CATEGORY_LABELS: Record<SvgCategory, string> = {
  [SVG_CATEGORIES.ICONS]: 'Icons',
  [SVG_CATEGORIES.PEOPLE]: 'People Images',
  [SVG_CATEGORIES.SHAPES]: 'Shape Images',
};

export const ALL_SVG_CATEGORIES: SvgCategory[] = Object.values(SVG_CATEGORIES);

export interface IDocumentDTO {
  id: number;
  name: string;
  filename: string;
  extension: string;
  path: string;
  isGlobal: boolean;
  category?: SvgCategory;
}

export interface IClientFileDTO {
  cloudStorageKey: string;
  path: string;
  extension: string;
  createdAt: string;
}

export interface IDocumentFailedDTO {
  uploadFileId: number;
  message: string;
  fileName: string;
  documentName: string | null;
  originalIndex?: number;
}

export interface IUploadImageResponseData {
  error?: IDocumentFailedDTO[];
  success?: IDocumentDTO[];
}

export interface IPaginationDocumentDTO {
  data: IDocumentDTO[];
  meta: IPaginationMetaProps;
}

export interface IGetListDocumentsByCompanyIdProps {
  clientId: string;
  page: number;
  limit: number;
  filter: string;
}
export interface IGetDocumentsByCompanyIdProps {
  clientId: string;
}

export type IUploadDocumentProps = Pick<IDocumentDTO, 'id'> & {
  newImage: File;
};

export interface IRenameDocumentProps {
  documentId: string;
  newName: string;
}

export interface IDeleteDocumentProps {
  documentId: string;
}

export const getDocumentByID = async (documentId: string): Promise<string> => {
  const result = await api
    .get<string>(`api/documents/${documentId}`, {
      responseType: 'arraybuffer',
    })
    .then((resp) => {
      const raw64 = Buffer.from(resp.data, 'binary').toString('base64');
      return `data:${resp.headers['content-type']};base64, ${raw64}`;
    })
    .catch((err) => {
      throw err;
    });

  return result;
};

export const getListDocumentsByClient = async ({
  clientId,
  page,
  limit,
  filter,
}: IGetListDocumentsByCompanyIdProps): Promise<IPaginationDocumentDTO> => {
  const result = await api
    .get<IPaginationResponseData<IDocumentDTO[]>>(
      `api/documents/list/company/${clientId}?page=${page}&limit=${limit}&filter=${encodeURIComponent(
        filter
      )}`
    )
    .then((resp) => resp.data)
    .catch((err) => {
      throw err;
    });

  return result;
};

export const uploadDocument = async ({
  id,
  newImage,
}: IUploadDocumentProps): Promise<IDocumentDTO> => {
  const data = new FormData();
  data.append('document', newImage, newImage.name);
  const result = await api
    .post<IResponseData<IDocumentDTO>>(`api/documents/company/${id}`, data)
    .then((resp) => resp.data.data)
    .catch((err) => {
      throw err;
    });

  return result;
};

export const renameDocument = async ({
  documentId,
  newName,
}: IRenameDocumentProps): Promise<IDocumentDTO> => {
  const result = await api
    .patch<IResponseData<IDocumentDTO>>(`api/documents/${documentId}`, {
      name: newName,
    })
    .then((resp) => resp.data.data)
    .catch((err) => {
      throw err;
    });

  return result;
};

export const deleteDocument = async ({
  documentId,
}: IDeleteDocumentProps): Promise<{ message: string }> => {
  const result = await api
    .delete<IResponseData>(`api/documents/${documentId}`)
    .then((resp) => resp.data.data)
    .catch((err) => {
      throw err;
    });

  return result;
};

export const globalDeleteDocument = async ({
  documentId,
}: IDeleteDocumentProps): Promise<{ message: string }> => {
  const result = await api
    .delete<IResponseData>(`${globalDocumentPath}/${documentId}`)
    .then((resp) => resp.data.data)
    .catch((err) => {
      throw err;
    });
  return result;
};

export const courseDeleteDocument = async ({
  documentId,
}: IDeleteDocumentProps): Promise<{ message: string }> => {
  const result = await api
    .delete<IResponseData>(`/api/image-library-course/${documentId}`)
    .then((resp) => resp.data.data)
    .catch((err) => {
      throw err;
    });
  return result;
};

export const templateDeleteDocument = async ({
  documentId,
}: IDeleteDocumentProps): Promise<{ message: string }> => {
  const result = await api
    .delete<IResponseData>(`/api/image-library-template/${documentId}`)
    .then((resp) => resp.data.data)
    .catch((err) => {
      throw err;
    });
  return result;
};

export interface IUpdateDocumentCategoryProps {
  documentId: string;
  category: SvgCategory;
}

export interface IUpdateDocumentProps {
  documentId: string;
  category?: SvgCategory;
  documentName?: string;
}

const updateDocumentCategory = async (
  path: string,
  { documentId, category }: IUpdateDocumentCategoryProps
): Promise<IDocumentDTO> => {
  const result = await api
    .patch<IResponseData<IDocumentDTO>>(`${path}/${documentId}`, { category })
    .then((resp) => resp.data.data)
    .catch((err) => {
      throw err;
    });
  return result;
};

const updateDocument = async (
  path: string,
  { documentId, category, documentName }: IUpdateDocumentProps
): Promise<IDocumentDTO> => {
  const data: { category?: SvgCategory; documentName?: string } = {};
  if (category !== undefined) data.category = category;
  if (documentName !== undefined) data.documentName = documentName;

  const result = await api
    .patch<IResponseData<IDocumentDTO>>(`${path}/${documentId}`, data)
    .then((resp) => resp.data.data)
    .catch((err) => {
      throw err;
    });
  return result;
};

export const globalUpdateDocumentCategory = (
  props: IUpdateDocumentCategoryProps
) => updateDocumentCategory(globalDocumentPath, props);

export const courseUpdateDocumentCategory = (
  props: IUpdateDocumentCategoryProps
) => updateDocumentCategory('/api/image-library-course', props);

export const templateUpdateDocumentCategory = (
  props: IUpdateDocumentCategoryProps
) => updateDocumentCategory('/api/image-library-template', props);

export const globalUpdateDocument = (props: IUpdateDocumentProps) =>
  updateDocument(globalDocumentPath, props);

export const courseUpdateDocument = (props: IUpdateDocumentProps) =>
  updateDocument('/api/image-library-course', props);

export const templateUpdateDocument = (props: IUpdateDocumentProps) =>
  updateDocument('/api/image-library-template', props);
